import {Fragment, type CSSProperties, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {Check, MapPin} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {useArrivalProperty} from 'components/arrival';
import {useChekinSDK} from 'context/ChekinSDK';
import {useLateStart} from './useLateStart';
import styles from './entrance.module.css';

const WORDS_START = 0.55;
const WORD_STEP = 0.09;
const TICK_START = 1;
const TICK_STEP = 0.55;
const TICK_HOLD = 0.5;

type Props = {
  startedAt: number;
};

function RevealTitle({name, startedAt}: Props & {name: string}) {
  const late = useLateStart(true, startedAt);

  return (
    <h1 className={styles.title}>
      {name.split(' ').map((word, index) => (
        <Fragment key={`${word}-${index}`}>
          {index > 0 && ' '}
          <span className={styles.word}>
            <span
              style={
                {
                  '--d': `${Math.max(
                    index * WORD_STEP,
                    WORDS_START + index * WORD_STEP - late,
                  ).toFixed(2)}s`,
                } as CSSProperties
              }
            >
              {word}
            </span>
          </span>
        </Fragment>
      ))}
    </h1>
  );
}

function EntranceReveal({startedAt}: Props) {
  const {t} = useTranslation();
  const property = useArrivalProperty();
  const {accountName} = useChekinSDK();
  const loadedName = property.isLoading ? '' : property.name || accountName;
  const name = property.isLoading ? '' : loadedName || t('arrival_online_check_in');
  const location = [property.city, property.country].filter(Boolean).join(', ');

  const ticks: ReactNode[] = [
    loadedName
      ? t('entrance_opening_property', {name: loadedName})
      : t('entrance_opening_check_in'),
    location && (
      <>
        <MapPin size={14} aria-hidden />
        {location}
      </>
    ),
    t('entrance_preparing_check_in'),
    <>
      <Check size={14} strokeWidth={3} aria-hidden />
      {t('entrance_ready_to_find_booking')}
    </>,
  ].filter(Boolean);

  return (
    <div className={styles.content} data-entrance="content">
      <p className={styles.eyebrow}>{t('arrival_welcome_to')}</p>
      {name ? (
        <RevealTitle key={name} name={name} startedAt={startedAt} />
      ) : (
        <h1 className={styles.title}>&nbsp;</h1>
      )}
      <p className={styles.ticker} aria-hidden>
        {ticks.map((tick, index) => {
          const isLast = index === ticks.length - 1;
          const start = TICK_START + index * TICK_STEP + (index ? TICK_HOLD : 0);
          const out = TICK_START + (index + 1) * TICK_STEP - 0.3 + TICK_HOLD;

          return (
            <span
              key={index}
              className={cn(isLast && styles.last)}
              style={{'--d': `${start}s`, '--o': `${out}s`} as CSSProperties}
            >
              {tick}
            </span>
          );
        })}
      </p>
    </div>
  );
}

export {EntranceReveal};
